import React, { useState, useEffect, useCallback } from "react";
import {
  Card,
  Descriptions,
  Tag,
  Button,
  Space,
  Spin,
  message,
  Progress,
  Row,
  Col,
  Statistic,
  Switch,
  Modal,
  Typography,
  Alert,
  Divider,
  Tooltip,
  Table,
  InputNumber,
  Input,
  Empty,
} from "antd";
import {
  CrownOutlined,
  ThunderboltOutlined,
  SettingOutlined,
  ExclamationCircleOutlined,
  CloseCircleOutlined,
  InfoCircleOutlined,
  RiseOutlined,
  BankOutlined,
  CreditCardOutlined,
  FileTextOutlined,
  ShopOutlined,
  UserOutlined,
  PlusOutlined,
  MinusOutlined,
  WalletOutlined,
  EditOutlined,
  TeamOutlined,
  CheckCircleOutlined,
  LockOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";

import governanceService from "@services/operator/governanceService";
import ListPageHeader from "@shared/ListPageHeader";
import { formatDate } from "@utils/dateTime";
import { getPaymentMethodsUIForSubscription } from "@config/governance/paymentMethods";
import { useOperatorAuth } from "@contexts/operator/OperatorAuthContext";

const { Text, Title } = Typography;

/**
 * TrialActivationAlert Component
 */
const TrialActivationAlert = ({
  account,
  subscription,
  onBankTransferClick,
  onCreditCardCheckout,
  payNowLoading,
  t,
}) => {
  const countryCode =
    account?.billing_profile?.country_code || account?.country_code;
  const billingCycle = subscription?.billing_cycle || "monthly";
  const amount = subscription?.current_price || 0;
  const uiConfig = getPaymentMethodsUIForSubscription(
    countryCode,
    billingCycle,
    amount,
  );
  const isExpired = account?.trial_expired;
  const hasPaymentMethod = account?.has_payment_method;

  const PayNowButton = () => (
    <Button
      type="primary"
      icon={<CreditCardOutlined />}
      onClick={onCreditCardCheckout}
      loading={payNowLoading}
    >
      {t("operator.governance.invoices.pay_now_button")}
    </Button>
  );

  const SetupCreditCardLink = ({ primary = true }) => (
    <Link to="/governance/payment_methods">
      <Button
        type={primary ? "primary" : "default"}
        icon={<CreditCardOutlined />}
      >
        {t("operator.governance.subscription.setup_credit_card")}
      </Button>
    </Link>
  );

  const PaymentButtons = () => (
    <Space style={{ marginTop: 8 }}>
      {uiConfig.onlyBankTransfer && (
        <Button
          type="primary"
          icon={<BankOutlined />}
          onClick={onBankTransferClick}
        >
          {t("operator.governance.subscription.pay_with_bank_transfer")}
        </Button>
      )}
      {!uiConfig.onlyBankTransfer && hasPaymentMethod && (
        <>
          <PayNowButton />
          {uiConfig.showBankTransfer && (
            <Button icon={<BankOutlined />} onClick={onBankTransferClick}>
              {t("operator.governance.subscription.bank_transfer")}
            </Button>
          )}
        </>
      )}
      {!uiConfig.onlyBankTransfer && !hasPaymentMethod && (
        <>
          {uiConfig.creditCardPrimary ? (
            <>
              <SetupCreditCardLink primary />
              {uiConfig.showBankTransfer && (
                <Button icon={<BankOutlined />} onClick={onBankTransferClick}>
                  {t("operator.governance.subscription.bank_transfer")}
                </Button>
              )}
            </>
          ) : (
            <>
              {uiConfig.showBankTransfer && (
                <Button
                  type="primary"
                  icon={<BankOutlined />}
                  onClick={onBankTransferClick}
                >
                  {t("operator.governance.subscription.bank_transfer")}
                </Button>
              )}
              <SetupCreditCardLink primary={false} />
            </>
          )}
        </>
      )}
    </Space>
  );

  return (
    <Alert
      message={
        isExpired
          ? t("operator.governance.subscription.trial_expired_title")
          : t("operator.governance.subscription.trial_activation_title")
      }
      description={
        <div>
          <p>
            {isExpired
              ? t("operator.governance.subscription.trial_expired_desc")
              : t("operator.governance.subscription.trial_activation_desc")}
          </p>
          <PaymentButtons />
        </div>
      }
      type={isExpired ? "error" : "warning"}
      showIcon
      icon={isExpired ? <CloseCircleOutlined /> : <ExclamationCircleOutlined />}
      style={{ marginBottom: 16 }}
    />
  );
};

/**
 * SuspendedAccountAlert Component
 * 帳戶暫停時顯示：綁卡引導 + 重啟預覽 + 確認付款
 */
const SuspendedAccountAlert = ({ account, onReactivated, t }) => {
  const navigate = useNavigate();
  const [previewLoading, setPreviewLoading] = useState(false);
  const [reactivateLoading, setReactivateLoading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const hasPaymentMethod = account?.has_payment_method;
  // Auto-fetch preview on mount to detect bank_transfer requirement (e.g. TW Pro 年繳 ≥ NT$200,000)
  // — controls whether to show "綁卡" or 直接走匯款 reactivation flow.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await governanceService.getReactivationPreview();
        if (!cancelled && res.success) setPreview(res.data);
      } catch (e) {
        // Silent: button falls back to credit_card path requiring card.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const requiresBankTransfer = preview?.requires_bank_transfer === true;

  const handlePreview = async () => {
    if (preview) {
      setShowConfirmModal(true);
      return;
    }
    setPreviewLoading(true);
    try {
      const res = await governanceService.getReactivationPreview();
      if (res.success) {
        setPreview(res.data);
        setShowConfirmModal(true);
      } else {
        message.error(
          res.message ||
            t("operator.governance.account.reactivation_preview_failed"),
        );
      }
    } catch (e) {
      message.error(
        t("operator.governance.account.reactivation_preview_failed"),
      );
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleReactivate = async () => {
    setReactivateLoading(true);
    try {
      const res = await governanceService.reactivateAccount();
      if (res.success) {
        setShowConfirmModal(false);
        if (res.data?.requires_payment_notification) {
          // Bank transfer path: invoice 已開立，導向發票清單讓貨主匯款 + 提交匯款通知
          // operator router 沒有 detail route，統一走清單頁（清單行內可開 PaymentNotification）
          message.success(
            t("operator.governance.account.reactivation_invoice_created"),
          );
          navigate("/governance/invoices");
        } else {
          message.success(
            t("operator.governance.account.reactivation_success"),
          );
          onReactivated?.();
        }
      } else {
        message.error(
          res.message || t("operator.governance.account.reactivation_failed"),
        );
      }
    } catch (e) {
      message.error(t("operator.governance.account.reactivation_failed"));
    } finally {
      setReactivateLoading(false);
    }
  };

  const formatCurrency = (amount, currency = "TWD") => {
    if (currency === "TWD") return `NT$${Number(amount).toLocaleString()}`;
    return `$${Number(amount).toLocaleString()}`;
  };

  return (
    <>
      <Alert
        message={t("operator.governance.account.suspended_title")}
        description={
          <div>
            <p>
              {requiresBankTransfer
                ? t("operator.governance.account.suspended_desc_bank_transfer")
                : t("operator.governance.account.suspended_desc")}
            </p>
            <Space style={{ marginTop: 8 }}>
              {requiresBankTransfer ? (
                // 金額 ≥ ATM 門檻：直接走匯款 reactivation，不要求綁卡
                <Button
                  type="primary"
                  icon={<BankOutlined />}
                  onClick={handlePreview}
                  loading={previewLoading}
                >
                  {t("operator.governance.account.reactivate_button")}
                </Button>
              ) : !hasPaymentMethod ? (
                <Link to="/governance/payment_methods">
                  <Button type="primary" icon={<CreditCardOutlined />}>
                    {t("operator.governance.subscription.setup_credit_card")}
                  </Button>
                </Link>
              ) : (
                <Button
                  type="primary"
                  icon={<ThunderboltOutlined />}
                  onClick={handlePreview}
                  loading={previewLoading}
                >
                  {t("operator.governance.account.reactivate_button")}
                </Button>
              )}
            </Space>
          </div>
        }
        type="error"
        showIcon
        icon={<CloseCircleOutlined />}
        style={{ marginBottom: 16 }}
      />

      <Modal
        title={t("operator.governance.account.reactivation_confirm_title")}
        open={showConfirmModal}
        onCancel={() => setShowConfirmModal(false)}
        footer={[
          <Button key="cancel" onClick={() => setShowConfirmModal(false)}>
            {t("operator.governance.account.reactivation_cancel")}
          </Button>,
          <Button
            key="confirm"
            type="primary"
            onClick={handleReactivate}
            loading={reactivateLoading}
          >
            {requiresBankTransfer
              ? t(
                  "operator.governance.account.reactivation_confirm_bank_transfer",
                )
              : t("operator.governance.account.reactivation_confirm_pay")}
          </Button>,
        ]}
        width={520}
      >
        {preview && (
          <div>
            {preview.forgiven_subscription_invoices?.length > 0 && (
              <Descriptions
                column={1}
                size="small"
                bordered
                style={{ marginBottom: 16 }}
              >
                {preview.forgiven_subscription_invoices.map((inv) => (
                  <Descriptions.Item
                    key={inv.id}
                    label={
                      <span>
                        {t("operator.governance.account.forgiven_fees")}
                        <br />
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          {inv.billing_period_start} ~ {inv.billing_period_end}
                        </Text>
                      </span>
                    }
                  >
                    <Text type="success" delete>
                      {formatCurrency(inv.total)}
                    </Text>
                    <Tag color="green" style={{ marginLeft: 8 }}>
                      {t("operator.governance.account.forgiven_tag")}
                    </Tag>
                  </Descriptions.Item>
                ))}
              </Descriptions>
            )}

            <Descriptions column={1} size="small" bordered>
              {preview.outstanding_usage_fees > 0 && (
                <Descriptions.Item
                  label={t("operator.governance.account.outstanding_usage")}
                >
                  {formatCurrency(preview.outstanding_usage_fees)}
                </Descriptions.Item>
              )}
              {(preview.current_period_subscription_fee > 0 ||
                preview.new_subscription_fee > 0) && (
                <Descriptions.Item
                  label={t("operator.governance.account.new_subscription_fee")}
                >
                  <div>
                    {formatCurrency(
                      preview.current_period_subscription_fee > 0
                        ? preview.current_period_subscription_fee
                        : preview.new_subscription_fee,
                    )}
                    {preview.new_billing_period?.start && (
                      <Text type="secondary" style={{ marginLeft: 8 }}>
                        ({preview.new_billing_period.start} ~{" "}
                        {preview.new_billing_period.end})
                      </Text>
                    )}
                    {preview.proration?.is_prorated && (
                      <div>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          {t("operator.governance.account.prorated_info", {
                            remaining: preview.proration.remaining_days,
                            total: preview.proration.total_days,
                            full_price: formatCurrency(
                              preview.proration.full_price,
                            ),
                          })}
                        </Text>
                      </div>
                    )}
                  </div>
                </Descriptions.Item>
              )}
              {preview.subtotal > 0 && preview.tax?.tax_amount > 0 && (
                <Descriptions.Item
                  label={t("operator.governance.account.subtotal")}
                >
                  {formatCurrency(preview.subtotal)}
                </Descriptions.Item>
              )}
              {preview.tax?.tax_amount > 0 && (
                <Descriptions.Item
                  label={
                    preview.tax.tax_name
                      ? `${preview.tax.tax_name} (${(preview.tax.rate * 100).toFixed(0)}%)`
                      : t("operator.governance.account.tax")
                  }
                >
                  {formatCurrency(preview.tax.tax_amount)}
                </Descriptions.Item>
              )}
              <Descriptions.Item
                label={
                  <Text strong>
                    {t("operator.governance.account.total_due")}
                  </Text>
                }
              >
                <Text strong style={{ fontSize: 16, color: "#1890ff" }}>
                  {formatCurrency(preview.total_due)}
                </Text>
              </Descriptions.Item>
            </Descriptions>

            {preview.requires_bank_transfer && (
              <Alert
                style={{ marginTop: 16 }}
                type="info"
                showIcon
                icon={<BankOutlined />}
                message={t(
                  "operator.governance.account.bank_transfer_required_title",
                )}
                description={
                  <div>
                    <p style={{ marginBottom: 8 }}>
                      {t(
                        "operator.governance.account.bank_transfer_required_desc",
                      )}
                    </p>
                    {preview.bank_transfer_info && (
                      <Descriptions column={1} size="small">
                        {preview.bank_transfer_info.bank_name && (
                          <Descriptions.Item
                            label={t("operator.governance.account.bank_name")}
                          >
                            {preview.bank_transfer_info.bank_name}
                          </Descriptions.Item>
                        )}
                        {preview.bank_transfer_info.account_name && (
                          <Descriptions.Item
                            label={t(
                              "operator.governance.account.bank_account_name",
                            )}
                          >
                            {preview.bank_transfer_info.account_name}
                          </Descriptions.Item>
                        )}
                        {preview.bank_transfer_info.account_number && (
                          <Descriptions.Item
                            label={t(
                              "operator.governance.account.bank_account_number",
                            )}
                          >
                            <Text copyable>
                              {preview.bank_transfer_info.account_number}
                            </Text>
                          </Descriptions.Item>
                        )}
                      </Descriptions>
                    )}
                  </div>
                }
              />
            )}
          </div>
        )}
      </Modal>
    </>
  );
};

/**
 * TopUpModal Component - AI Account Top-up
 */
const TopUpModal = ({ visible, onClose, onSuccess, t }) => {
  const navigate = useNavigate();
  const [packs, setPacks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedPack, setSelectedPack] = useState(null);
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [purchasing, setPurchasing] = useState(false);
  const [currency, setCurrency] = useState("");

  // Fetch packs when modal opens
  useEffect(() => {
    if (visible) {
      const fetchPacks = async () => {
        setLoading(true);
        try {
          const res = await governanceService.getAiCreditPacks();
          if (res.success) {
            setPacks(res.data.packs || []);
            setCurrency(res.data.currency || "");
          }
        } catch (error) {
          console.error("Failed to fetch packs:", error);
          message.error(t("common.error.fetch_failed"));
        } finally {
          setLoading(false);
        }
      };
      fetchPacks();
      setSelectedPack(null);
      setPreview(null);
    }
  }, [visible, t]);

  // Fetch preview when pack is selected
  useEffect(() => {
    if (selectedPack) {
      const fetchPreview = async () => {
        setPreviewLoading(true);
        try {
          const res = await governanceService.purchaseAiCredits(
            selectedPack.id,
            false,
          );
          if (res.success) {
            setPreview(res.data);
          }
        } catch (error) {
          console.error("Failed to fetch preview:", error);
        } finally {
          setPreviewLoading(false);
        }
      };
      fetchPreview();
    } else {
      setPreview(null);
    }
  }, [selectedPack]);

  const handleConfirm = async () => {
    if (!selectedPack) return;

    setPurchasing(true);
    try {
      const res = await governanceService.purchaseAiCredits(
        selectedPack.id,
        true,
      );

      if (res.success) {
        onClose();
        const paymentResult = res.data.payment_result;

        if (paymentResult?.status === "paid") {
          message.success(t("operator.governance.usage.purchase_success"));
          onSuccess?.();
        } else if (paymentResult?.status === "payment_failed") {
          message.warning(
            t("operator.governance.usage.payment_failed_manual_required"),
          );
          onSuccess?.();
          navigate("/governance/invoices");
        } else {
          message.info(t("operator.governance.usage.purchase_pending"));
          onSuccess?.();
          navigate("/governance/invoices");
        }
      } else {
        const errorKey = `operator.governance.usage.${res.message}`;
        const translated = t(errorKey, { defaultValue: "" });
        message.error(
          translated ||
            res.message ||
            t("operator.governance.usage.purchase_failed"),
        );
      }
    } catch (error) {
      console.error("Failed to purchase credits:", error);
      message.error(t("operator.governance.usage.purchase_failed"));
    } finally {
      setPurchasing(false);
    }
  };

  const currencySymbol = currency === "TWD" ? "NT$" : "$";

  return (
    <Modal
      title={
        <Space>
          <WalletOutlined />
          {t("operator.governance.usage.top_up_title")}
        </Space>
      }
      open={visible}
      onOk={handleConfirm}
      onCancel={onClose}
      confirmLoading={purchasing}
      okText={t("operator.governance.usage.confirm_purchase")}
      okButtonProps={{ disabled: !selectedPack }}
      cancelText={t("common.button.cancel")}
      width={560}
    >
      {loading ? (
        <div style={{ textAlign: "center", padding: 40 }}>
          <Spin />
        </div>
      ) : packs.length === 0 ? (
        <Empty description={t("operator.governance.usage.no_packs")} />
      ) : (
        <>
          <Text type="secondary">
            {t("operator.governance.usage.top_up_desc")}
          </Text>
          <div style={{ marginTop: 16 }}>
            {packs.map((pack) => (
              <div
                key={pack.id}
                onClick={() => setSelectedPack(pack)}
                style={{
                  padding: "12px 16px",
                  marginBottom: 8,
                  border:
                    selectedPack?.id === pack.id
                      ? "2px solid #1890ff"
                      : "1px solid #f0f0f0",
                  borderRadius: 8,
                  cursor: "pointer",
                  backgroundColor:
                    selectedPack?.id === pack.id ? "#e6f7ff" : "#fff",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <div>
                  <Text strong style={{ fontSize: 16 }}>
                    {pack.name}
                  </Text>
                </div>
                <div style={{ textAlign: "right" }}>
                  {pack.bonus_percentage > 0 && (
                    <Tag color="green">
                      +{pack.bonus_percentage}%{" "}
                      {t("operator.governance.usage.pack_bonus")}
                    </Tag>
                  )}
                </div>
              </div>
            ))}
          </div>

          {previewLoading ? (
            <div style={{ textAlign: "center", padding: 16 }}>
              <Spin size="small" />
            </div>
          ) : preview && !preview.requires_confirmation ? null : (
            preview && (
              <>
                <Divider />
                <Descriptions bordered column={1} size="small">
                  <Descriptions.Item
                    label={t("operator.governance.usage.pack_name")}
                  >
                    {preview.pack_name || selectedPack?.name}
                  </Descriptions.Item>
                  <Descriptions.Item
                    label={t("operator.governance.usage.pack_credits")}
                  >
                    {currencySymbol} {preview.credits?.toLocaleString()}
                  </Descriptions.Item>
                  {preview.bonus_credits > 0 && (
                    <Descriptions.Item
                      label={t("operator.governance.usage.pack_bonus")}
                    >
                      <Text style={{ color: "#52c41a" }}>
                        + {currencySymbol}{" "}
                        {preview.bonus_credits?.toLocaleString()}
                      </Text>
                    </Descriptions.Item>
                  )}
                  <Descriptions.Item
                    label={t("operator.governance.usage.pack_subtotal")}
                  >
                    {currencySymbol}{" "}
                    {(preview.subtotal || preview.amount)?.toLocaleString()}
                  </Descriptions.Item>
                  {preview.tax > 0 && (
                    <Descriptions.Item
                      label={t("operator.governance.usage.pack_tax", {
                        rate: (preview.tax_rate * 100).toFixed(0),
                      })}
                    >
                      {currencySymbol} {preview.tax?.toLocaleString()}
                    </Descriptions.Item>
                  )}
                  <Descriptions.Item
                    label={t("operator.governance.usage.pack_price")}
                  >
                    <Text strong style={{ fontSize: 18, color: "#1890ff" }}>
                      {currencySymbol} {preview.total?.toLocaleString()}
                    </Text>
                  </Descriptions.Item>
                  <Descriptions.Item
                    label={t("operator.governance.usage.payment_method")}
                  >
                    {preview.payment_method === "bank_transfer" ? (
                      <Space>
                        <BankOutlined />
                        {t(
                          "operator.governance.usage.payment_method_bank_transfer",
                        )}
                      </Space>
                    ) : (
                      <Space>
                        <CreditCardOutlined />
                        {t(
                          "operator.governance.usage.payment_method_credit_card",
                        )}
                      </Space>
                    )}
                  </Descriptions.Item>
                </Descriptions>

                {preview.payment_method === "bank_transfer" && (
                  <Alert
                    message={t("operator.governance.addons.payment_notice_atm")}
                    type="info"
                    showIcon
                    style={{ marginTop: 16 }}
                  />
                )}
              </>
            )
          )}
        </>
      )}
    </Modal>
  );
};

const STATUS_COLORS = {
  active: "green",
  trialing: "blue",
  trial: "blue",
  past_due: "orange",
  canceled: "red",
  cancelled: "red",
  suspended: "red",
  expired: "default",
  paused: "default",
};

const PLAN_TYPE_COLORS = {
  standard: "default",
  pro: "blue",
  enterprise: "gold",
};

// 帳號層級的「終止 / kill switch」狀態：一旦進入，整個服務停掉，
// subscription.status 的細節（trialing / past_due...）已無意義，方案卡片那顆
// Tag 必須改用與右上角同一個 account.statuses key，保證兩處文字一致。
// 注意：active / trial 不在此列 —— 那兩種狀態下帳號仍可用，必須讓
// subscription 的細節（如排程退訂 cancelled、付款逾期 past_due）透出。
//
// ⚠️ 同步警示：此名單對應後端 Governance::Account enum 的終止態
// (app/models/governance/account.rb: suspended/cancelled/closed)。
// 後端若新增任何帳號終止狀態，必須同步加進此 array，否則方案卡片 Tag
// 會靜默落回 subscription 細節、與右上角帳號狀態文字再次不一致
// (= PR-26052802 bug 復發，且無告警)。
const ACCOUNT_TERMINAL_STATUSES = ["suspended", "cancelled", "closed"];

const Subscription = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { checkAuthStatus } = useOperatorAuth();
  const [loading, setLoading] = useState(true);
  const [subscription, setSubscription] = useState(null);
  const [account, setAccount] = useState(null);
  const [usageSummary, setUsageSummary] = useState(null);
  const [aiCreditsWallet, setAiCreditsWallet] = useState(null);
  const [billingPeriod, setBillingPeriod] = useState(null);
  const [updating, setUpdating] = useState(false);
  const [cancelModalVisible, setCancelModalVisible] = useState(false);
  const [checkoutModalVisible, setCheckoutModalVisible] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [payNowLoading, setPayNowLoading] = useState(false);
  const [topUpModalVisible, setTopUpModalVisible] = useState(false);
  const [budgetModalVisible, setBudgetModalVisible] = useState(false);
  const [budgetValue, setBudgetValue] = useState(null);
  const [budgetUnlimited, setBudgetUnlimited] = useState(true);
  const [updatingBudget, setUpdatingBudget] = useState(false);
  const [salesReferralCode, setSalesReferralCode] = useState("");
  const [bindingReferral, setBindingReferral] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [subResponse, usageResponse] = await Promise.all([
        governanceService.getSubscription(),
        governanceService.getUsageSummary(),
      ]);

      if (subResponse.success) {
        setSubscription(subResponse.data.subscription);
        setAccount(subResponse.data.account);
      }

      if (usageResponse.success) {
        setUsageSummary(usageResponse.data.summary);
        setAiCreditsWallet(usageResponse.data.ai_credits_wallet);
        setBillingPeriod(usageResponse.data.billing_period);
      }
    } catch (error) {
      console.error("Failed to fetch subscription data:", error);
      message.error(t("common.error.fetch_failed"));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleOpenBudgetModal = () => {
    const isUnlimited = aiCreditsWallet?.daily_budget_unlimited;
    setBudgetUnlimited(isUnlimited);
    setBudgetValue(isUnlimited ? null : aiCreditsWallet?.daily_budget_limit);
    setBudgetModalVisible(true);
  };

  const handleSaveBudget = async () => {
    const limit = budgetUnlimited ? -1 : budgetValue || 0;
    try {
      setUpdatingBudget(true);
      const response = await governanceService.updateDailyBudget(limit);
      if (response.success) {
        message.success(t("operator.governance.usage.budget_updated"));
        setBudgetModalVisible(false);
        fetchData();
      } else {
        message.error(response.message || t("common.error.update_failed"));
      }
    } catch (error) {
      console.error("Failed to update daily budget:", error);
      message.error(t("common.error.update_failed"));
    } finally {
      setUpdatingBudget(false);
    }
  };

  const handleBindSalesReferral = async () => {
    if (!salesReferralCode.trim()) return;
    try {
      setBindingReferral(true);
      const response = await governanceService.bindSalesReferralCode(
        salesReferralCode.trim(),
      );
      if (response.success) {
        message.success(t("operator.governance.sales_referral.bind_success"));
        setSalesReferralCode("");
        fetchData();
      } else {
        message.error(
          response.message ||
            t("operator.governance.sales_referral.bind_failed"),
        );
      }
    } catch (error) {
      console.error("Failed to bind sales referral code:", error);
      message.error(t("operator.governance.sales_referral.bind_failed"));
    } finally {
      setBindingReferral(false);
    }
  };

  const handleAutoRenewToggle = async (checked) => {
    try {
      setUpdating(true);
      const response = await governanceService.updateSubscription({
        auto_renew: checked,
      });
      if (response.success) {
        setSubscription({ ...subscription, auto_renew: checked });
        message.success(t("common.message.update_success"));
      } else {
        message.error(response.message || t("common.error.update_failed"));
      }
    } catch (error) {
      console.error("Failed to update auto renew:", error);
      message.error(t("common.error.update_failed"));
    } finally {
      setUpdating(false);
    }
  };

  const handleCancelSubscription = async () => {
    try {
      setUpdating(true);
      const response = await governanceService.cancelSubscription("", false);
      if (response.success) {
        setSubscription(response.data.subscription);
        setCancelModalVisible(false);
        message.success(t("operator.governance.subscription.cancel_success"));
      } else {
        message.error(response.message || t("common.error.update_failed"));
      }
    } catch (error) {
      console.error("Failed to cancel subscription:", error);
      message.error(t("common.error.update_failed"));
    } finally {
      setUpdating(false);
    }
  };

  const handleReactivate = async () => {
    try {
      setUpdating(true);
      const response = await governanceService.reactivateSubscription();
      if (response.success) {
        setSubscription(response.data.subscription);
        message.success(
          t("operator.governance.subscription.reactivate_success"),
        );
      } else {
        message.error(response.message || t("common.error.update_failed"));
      }
    } catch (error) {
      console.error("Failed to reactivate subscription:", error);
      message.error(t("common.error.update_failed"));
    } finally {
      setUpdating(false);
    }
  };

  const handleCancelPendingChange = async () => {
    try {
      setUpdating(true);
      const response = await governanceService.cancelPendingChange();
      if (response.success) {
        setSubscription(response.data.subscription);
        message.success(
          t("operator.governance.subscription.cancel_pending_change_success"),
        );
      } else {
        message.error(response.message || t("common.error.update_failed"));
      }
    } catch (error) {
      console.error("Failed to cancel pending change:", error);
      message.error(t("common.error.update_failed"));
    } finally {
      setUpdating(false);
    }
  };

  const handleRevertBillingCycleUpgrade = async () => {
    try {
      setUpdating(true);
      const response = await governanceService.revertBillingCycleUpgrade();
      if (response.success) {
        setSubscription(response.data.subscription);
        const subResponse = await governanceService.getSubscription();
        if (subResponse.success) {
          setAccount(subResponse.data.account);
        }
        message.success(
          t(
            "operator.governance.subscription.revert_billing_cycle_upgrade_success",
          ),
        );
      } else {
        message.error(response.message || t("common.error.update_failed"));
      }
    } catch (error) {
      console.error("Failed to revert billing cycle upgrade:", error);
      message.error(t("common.error.update_failed"));
    } finally {
      setUpdating(false);
    }
  };

  const handleBankTransferCheckout = async () => {
    if (!subscription) return;

    try {
      setCheckoutLoading(true);
      const response = await governanceService.createCheckout(
        subscription.pricing_plan.id,
        subscription.billing_cycle,
        "bank_transfer",
      );

      if (response.success) {
        setCheckoutModalVisible(false);
        message.success(t("operator.governance.subscription.checkout_created"));
        navigate("/governance/invoices");
      } else {
        message.error(response.message || t("common.error.create_failed"));
      }
    } catch (error) {
      console.error("Failed to create checkout:", error);
      message.error(t("common.error.create_failed"));
    } finally {
      setCheckoutLoading(false);
    }
  };

  const handleCreditCardCheckout = async () => {
    if (!subscription) return;

    try {
      setPayNowLoading(true);
      const response = await governanceService.createCheckout(
        subscription.pricing_plan.id,
        subscription.billing_cycle,
        "credit_card",
      );

      if (response.success && response.data.payment_status === "paid") {
        message.success(t("operator.governance.invoices.payment_successful"));
        await checkAuthStatus();
        fetchData();
      } else {
        message.error(response.message || t("common.error.create_failed"));
      }
    } catch (error) {
      console.error("Failed to process credit card checkout:", error);
      message.error(t("common.error.create_failed"));
    } finally {
      setPayNowLoading(false);
    }
  };

  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "400px",
        }}
      >
        <Spin size="large" />
      </div>
    );
  }

  if (!subscription) {
    return (
      <div>
        <Alert
          message={t("operator.governance.subscription.not_found")}
          description={t("operator.governance.subscription.not_found_desc")}
          type="info"
          showIcon
        />
      </div>
    );
  }

  const pricingPlan = subscription.pricing_plan;
  const isCanceled = subscription.cancel_requested_at !== null;
  const isTrial = account?.trial === true;
  const isEnterprise = pricingPlan?.plan_type === "enterprise";
  const accountStatusOverridesPlanTag = ACCOUNT_TERMINAL_STATUSES.includes(
    account?.status,
  );

  const usageColumns = [
    {
      title: t("operator.governance.usage.type"),
      dataIndex: "usage_type",
      key: "usage_type",
      render: (type) => {
        const label = t(`operator.governance.usage.types.${type}`);
        if (type === "pcs") {
          return (
            <Link to="/governance/pcs_ledger">
              <Space size={4}>
                {label}
                <FileTextOutlined style={{ fontSize: 11 }} />
              </Space>
            </Link>
          );
        }
        return label;
      },
    },
    {
      title: t("operator.governance.usage.used"),
      dataIndex: "quantity",
      key: "quantity",
      align: "right",
      render: (val) => val.toLocaleString(),
    },
    {
      title: t("operator.governance.usage.included"),
      dataIndex: "included_units",
      key: "included_units",
      align: "right",
      render: (val, record) => {
        if (record.unlimited) {
          return (
            <Tag color="gold" style={{ margin: 0 }}>
              {t("common.unlimited")}
            </Tag>
          );
        }
        return val?.toLocaleString() ?? "-";
      },
    },
    {
      title: t("operator.governance.usage.percentage"),
      dataIndex: "percentage_used",
      key: "percentage_used",
      align: "center",
      width: 200,
      render: (percent, record) => {
        if (record.unlimited) {
          return <Text type="secondary">-</Text>;
        }
        return (
          <Progress
            percent={Math.min(percent, 100)}
            status={
              percent > 100 ? "exception" : percent > 80 ? "active" : "normal"
            }
            size="small"
          />
        );
      },
    },
    {
      title: t("operator.governance.usage.overage"),
      dataIndex: "overage",
      key: "overage",
      align: "right",
      render: (val, record) => {
        if (record.unlimited) return "-";
        return val > 0 ? (
          <Text type="danger">{val.toLocaleString()}</Text>
        ) : (
          "-"
        );
      },
    },
    {
      title: t("operator.governance.usage.overage_amount"),
      dataIndex: "overage_amount",
      key: "overage_amount",
      align: "right",
      render: (val, record) => {
        if (record.unlimited) return "-";
        return val > 0 ? (
          <Text type="danger">NT$ {val.toLocaleString()}</Text>
        ) : (
          "-"
        );
      },
    },
  ];

  const subscriptionHeaderActions = (
    <>
      {account && (
        <Space split={<Divider type="vertical" />} size="small">
          <Text type="secondary">{account.account_number}</Text>
          <Tag color={STATUS_COLORS[account.status]} style={{ margin: 0 }}>
            {t(`operator.governance.account.statuses.${account.status}`)}
          </Tag>
          {account.trial && (
            <Tag
              color="blue"
              icon={<ThunderboltOutlined />}
              style={{ margin: 0 }}
            >
              {account.trial_days_remaining > 0
                ? `${t("operator.governance.account.trial_remaining")}: ${account.trial_days_remaining} ${t("common.days")}`
                : t("operator.governance.account.trial_expires_today")}
            </Tag>
          )}
        </Space>
      )}
      {!isEnterprise && (
        <Link to="/governance/pricing_plans">
          <Button type="primary" icon={<RiseOutlined />}>
            {t("operator.governance.subscription.change_plan")}
          </Button>
        </Link>
      )}
    </>
  );

  return (
    <div>
      <ListPageHeader
        items={["governance", "governance.subscription"]}
        actions={subscriptionHeaderActions}
      />

      {/* Trial Activation Alert */}
      {account?.trial && !isEnterprise && (
        <TrialActivationAlert
          account={account}
          subscription={subscription}
          onBankTransferClick={() => setCheckoutModalVisible(true)}
          onCreditCardCheckout={handleCreditCardCheckout}
          payNowLoading={payNowLoading}
          t={t}
        />
      )}

      {/* Suspended Account Alert */}
      {account?.status === "suspended" && !isEnterprise && (
        <SuspendedAccountAlert
          account={account}
          onReactivated={async () => {
            await checkAuthStatus();
            fetchData();
          }}
          t={t}
        />
      )}

      {/* Billing Cycle Upgrade Revert Alert */}
      {account?.can_revert_billing_cycle_upgrade && !isEnterprise && (
        <Alert
          message={t(
            "operator.governance.subscription.billing_cycle_upgrade_pending_title",
          )}
          description={
            <div>
              <p>
                {t(
                  "operator.governance.subscription.billing_cycle_upgrade_pending_desc",
                )}
              </p>
              <Space style={{ marginTop: 8 }}>
                <Button
                  type="primary"
                  onClick={() => navigate("/governance/invoices")}
                  icon={<FileTextOutlined />}
                >
                  {t("operator.governance.subscription.view_and_pay_invoice")}
                </Button>
                <Button
                  onClick={handleRevertBillingCycleUpgrade}
                  loading={updating}
                >
                  {t("operator.governance.subscription.revert_to_monthly")}
                </Button>
              </Space>
            </div>
          }
          type="info"
          showIcon
          icon={<InfoCircleOutlined />}
          style={{ marginBottom: 16 }}
        />
      )}

      {isEnterprise && (
        <Alert
          message={t("operator.governance.subscription.enterprise_notice")}
          description={t("operator.governance.subscription.enterprise_contact")}
          type="info"
          showIcon
          icon={<CrownOutlined />}
          style={{ marginBottom: 16 }}
        />
      )}

      {/* Subscription Info */}
      <Row gutter={16}>
        <Col span={16}>
          <Card
            title={t("operator.governance.subscription.current_plan")}
            size="small"
            extra={
              <Space>
                <Tag
                  color={PLAN_TYPE_COLORS[pricingPlan.plan_type]}
                  style={{ margin: 0 }}
                >
                  {t(`operator.governance.plan.types.${pricingPlan.plan_type}`)}
                </Tag>
                <Tag
                  color={
                    accountStatusOverridesPlanTag || account?.trial_expired
                      ? "red"
                      : STATUS_COLORS[subscription.status]
                  }
                  style={{ margin: 0 }}
                >
                  {accountStatusOverridesPlanTag
                    ? t(
                        `operator.governance.account.statuses.${account.status}`,
                      )
                    : account?.trial_expired
                      ? t(
                          "operator.governance.subscription.statuses.trial_expired",
                        )
                      : t(
                          `operator.governance.subscription.statuses.${subscription.status}`,
                        )}
                </Tag>
              </Space>
            }
          >
            {isTrial ? (
              <>
                {/* Trial: clean grid layout */}
                <Row gutter={[24, 16]}>
                  <Col span={6}>
                    <Text
                      type="secondary"
                      style={{
                        fontSize: 12,
                        display: "block",
                        marginBottom: 4,
                      }}
                    >
                      {t("operator.governance.subscription.trial_period")}
                    </Text>
                    <Text strong>
                      {formatDate(subscription.current_period_start)} ~{" "}
                      {formatDate(subscription.current_period_end)}
                    </Text>
                  </Col>
                  <Col span={6}>
                    <Text
                      type="secondary"
                      style={{
                        fontSize: 12,
                        display: "block",
                        marginBottom: 4,
                      }}
                    >
                      {t("operator.governance.subscription.current_price")}
                    </Text>
                    {account?.trial_expired ? (
                      <Text strong style={{ color: "#ff4d4f", fontSize: 16 }}>
                        {t("operator.governance.subscription.trial_ended")}
                      </Text>
                    ) : (
                      <Text strong style={{ color: "#52c41a", fontSize: 16 }}>
                        {t("operator.governance.subscription.trial_free")}
                      </Text>
                    )}
                  </Col>
                  <Col span={6}>
                    <Text
                      type="secondary"
                      style={{
                        fontSize: 12,
                        display: "block",
                        marginBottom: 4,
                      }}
                    >
                      {account?.trial_expired
                        ? t("operator.governance.subscription.payment_due")
                        : t(
                            "operator.governance.subscription.first_billing_date",
                          )}
                    </Text>
                    <Text
                      strong
                      style={
                        account?.trial_expired
                          ? { color: "#ff4d4f" }
                          : undefined
                      }
                    >
                      {formatDate(
                        account.next_billing_at ||
                          subscription.current_period_end,
                      )}
                    </Text>
                  </Col>
                  <Col span={6}>
                    <Text
                      type="secondary"
                      style={{
                        fontSize: 12,
                        display: "block",
                        marginBottom: 4,
                      }}
                    >
                      {t("operator.governance.subscription.auto_renew")}
                    </Text>
                    {!isEnterprise && (
                      <Switch
                        size="small"
                        checked={subscription.auto_renew}
                        onChange={handleAutoRenewToggle}
                        loading={updating}
                      />
                    )}
                  </Col>
                </Row>
                <Divider style={{ margin: "12px 0" }} />
                <Row gutter={[24, 0]} align="middle">
                  <Col>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {t(
                        "operator.governance.subscription.trial_billing_starts",
                      )}
                      :
                    </Text>
                  </Col>
                  <Col>
                    <Text style={{ fontSize: 13 }}>
                      NT$ {subscription.current_price?.toLocaleString()}
                    </Text>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {" "}
                      /{" "}
                      {t(
                        `operator.governance.subscription.cycles.${subscription.billing_cycle}`,
                      )}
                    </Text>
                  </Col>
                </Row>
              </>
            ) : (
              <Descriptions column={2} size="small">
                <Descriptions.Item
                  label={t("operator.governance.subscription.billing_cycle")}
                >
                  {t(
                    `operator.governance.subscription.cycles.${subscription.billing_cycle}`,
                  )}
                </Descriptions.Item>

                <Descriptions.Item
                  label={t("operator.governance.subscription.current_price")}
                >
                  <Text strong>
                    NT$ {subscription.current_price?.toLocaleString()}
                  </Text>
                  <Text type="secondary">
                    {" "}
                    /{" "}
                    {t(
                      `operator.governance.subscription.cycles.${subscription.billing_cycle}`,
                    )}
                  </Text>
                </Descriptions.Item>

                <Descriptions.Item
                  label={t("operator.governance.subscription.current_period")}
                >
                  {formatDate(subscription.current_period_start)} ~{" "}
                  {formatDate(subscription.current_period_end)}
                </Descriptions.Item>

                <Descriptions.Item
                  label={t(
                    "operator.governance.subscription.next_billing_date",
                  )}
                >
                  {formatDate(
                    account.next_billing_at || subscription.current_period_end,
                  )}
                </Descriptions.Item>

                {!isEnterprise && (
                  <Descriptions.Item
                    label={t("operator.governance.subscription.auto_renew")}
                  >
                    <Switch
                      size="small"
                      checked={subscription.auto_renew}
                      onChange={handleAutoRenewToggle}
                      loading={updating}
                      disabled={isCanceled}
                    />
                  </Descriptions.Item>
                )}
              </Descriptions>
            )}

            {isCanceled && (
              <Alert
                style={{ marginTop: 16 }}
                message={t("operator.governance.subscription.cancel_scheduled")}
                description={
                  <>
                    {t("operator.governance.subscription.cancel_effective_at")}:{" "}
                    {formatDate(subscription.cancel_effective_at)}
                    <br />
                    <Button
                      type="link"
                      onClick={handleReactivate}
                      loading={updating}
                      style={{ padding: 0, marginTop: 8 }}
                    >
                      {t("operator.governance.subscription.reactivate")}
                    </Button>
                  </>
                }
                type="warning"
                showIcon
                icon={<ExclamationCircleOutlined />}
              />
            )}

            {subscription.pending_change &&
              subscription.pending_pricing_plan && (
                <Alert
                  style={{ marginTop: 16 }}
                  message={t("operator.governance.subscription.pending_change")}
                  description={
                    <>
                      {t("operator.governance.subscription.pending_plan")}:{" "}
                      {t(
                        `operator.governance.plan.types.${subscription.pending_pricing_plan.plan_type}`,
                      )}
                      <br />
                      {t(
                        "operator.governance.subscription.effective_next_period",
                      )}
                      <br />
                      <Button
                        type="link"
                        onClick={handleCancelPendingChange}
                        loading={updating}
                        style={{ padding: 0, marginTop: 8 }}
                      >
                        {t(
                          "operator.governance.subscription.cancel_pending_change",
                        )}
                      </Button>
                    </>
                  }
                  type="info"
                  showIcon
                  icon={<InfoCircleOutlined />}
                />
              )}
          </Card>
        </Col>

        <Col span={8}>
          {/* Plan Features */}
          <Card
            title={t("operator.governance.plan.features_label")}
            size="small"
          >
            <Row gutter={[8, 4]}>
              <Col span={12}>
                <Text type="secondary">
                  {t("operator.governance.plan.max_oms")}
                </Text>
              </Col>
              <Col span={12} style={{ textAlign: "right" }}>
                <Text strong>
                  {pricingPlan.features.max_oms == null ||
                  pricingPlan.features.max_oms === -1
                    ? t("common.unlimited")
                    : pricingPlan.features.max_oms}
                </Text>
              </Col>
              <Col span={12}>
                <Text type="secondary">
                  {t("operator.governance.plan.base_warehouses")}
                </Text>
              </Col>
              <Col span={12} style={{ textAlign: "right" }}>
                <Text strong>
                  {subscription?.capacity?.total_warehouses ??
                    pricingPlan.features.base_warehouses}
                </Text>
              </Col>
              <Col span={12}>
                <Text type="secondary">
                  {t("operator.governance.plan.max_wms_users")}
                </Text>
              </Col>
              <Col span={12} style={{ textAlign: "right" }}>
                <Text strong>
                  {subscription?.capacity?.total_wms_users ??
                    pricingPlan.features.max_wms_users}
                </Text>
              </Col>
              <Col span={12}>
                <Text type="secondary">
                  {t("operator.governance.plan.included_pcs")}
                </Text>
              </Col>
              <Col span={12} style={{ textAlign: "right" }}>
                <Text strong>
                  {pricingPlan.features.included_pcs?.toLocaleString() || 0}
                </Text>
              </Col>
            </Row>

            {/* Cancel Subscription */}
            {!isCanceled &&
              subscription.status === "active" &&
              !isEnterprise && (
                <>
                  <Divider style={{ margin: "12px 0" }} />
                  <Button
                    danger
                    size="small"
                    block
                    onClick={() => setCancelModalVisible(true)}
                  >
                    {t("operator.governance.subscription.cancel")}
                  </Button>
                </>
              )}
          </Card>
        </Col>
      </Row>

      {/* Addon 功能已於 2026/05 移除（所有方案改為固定額度）。
          Enterprise 的客製化額度由後台維運直接調整 governance_subscriptions
          的 enterprise_warehouse_limit / enterprise_operator_limit 兩個欄位。 */}

      {/* Sales Referral — Enterprise 方案不適用 */}
      {account?.sales_referral_eligible !== false && (
        <Card
          title={
            <Space>
              <TeamOutlined />
              {t("operator.governance.sales_referral.title")}
            </Space>
          }
          size="small"
          style={{ marginTop: 16 }}
        >
          {account?.sales_referral ? (
            <Descriptions column={2} size="small">
              <Descriptions.Item
                label={t("operator.governance.sales_referral.code")}
              >
                <Space>
                  <Tag color="blue">{account.sales_referral.code}</Tag>
                  <LockOutlined style={{ color: "#8c8c8c", fontSize: 12 }} />
                </Space>
              </Descriptions.Item>
              <Descriptions.Item
                label={t("operator.governance.sales_referral.name")}
              >
                {account.sales_referral.name}
              </Descriptions.Item>
              {account.sales_referral.email && (
                <Descriptions.Item
                  label={t("operator.governance.sales_referral.email")}
                >
                  {account.sales_referral.email}
                </Descriptions.Item>
              )}
              {account.sales_referral.phone && (
                <Descriptions.Item
                  label={t("operator.governance.sales_referral.phone")}
                >
                  {account.sales_referral.phone}
                </Descriptions.Item>
              )}
            </Descriptions>
          ) : account?.sales_referral_bound ? (
            <Text type="secondary">
              {t("operator.governance.sales_referral.bound_no_detail")}
            </Text>
          ) : (
            <div>
              <Text
                type="secondary"
                style={{ display: "block", marginBottom: 12 }}
              >
                {t("operator.governance.sales_referral.description")}
              </Text>
              <Space.Compact style={{ maxWidth: 400 }}>
                <Input
                  placeholder={t(
                    "operator.governance.sales_referral.placeholder",
                  )}
                  value={salesReferralCode}
                  onChange={(e) => setSalesReferralCode(e.target.value)}
                  onPressEnter={handleBindSalesReferral}
                  disabled={bindingReferral}
                />
                <Button
                  type="primary"
                  icon={<CheckCircleOutlined />}
                  onClick={handleBindSalesReferral}
                  loading={bindingReferral}
                  disabled={!salesReferralCode.trim()}
                >
                  {t("operator.governance.sales_referral.bind")}
                </Button>
              </Space.Compact>
            </div>
          )}
        </Card>
      )}

      {/* Usage Summary */}
      {usageSummary && usageSummary.length > 0 && (
        <Card
          title={
            <Space>
              <ThunderboltOutlined />
              {t("operator.governance.usage.summary")}
              {billingPeriod?.start_date && billingPeriod?.end_date && (
                <Text
                  type="secondary"
                  style={{ fontWeight: "normal", fontSize: "12px" }}
                >
                  ({formatDate(billingPeriod.start_date)} ~{" "}
                  {formatDate(billingPeriod.end_date)})
                </Text>
              )}
            </Space>
          }
          size="small"
          style={{ marginTop: 16 }}
        >
          <Table
            // 暫時隱藏 orders / shipments：目前僅統計，未納入計費，避免使用者誤判
            dataSource={usageSummary?.filter(
              (row) => !["orders", "shipments"].includes(row.usage_type),
            )}
            columns={usageColumns}
            rowKey="usage_type"
            pagination={false}
            size="small"
          />
        </Card>
      )}

      {/* AI Account */}
      {aiCreditsWallet && (
        <Card
          title={
            <Space>
              <WalletOutlined />
              {t("operator.governance.usage.ai_credits_wallet")}
              {aiCreditsWallet.currency && (
                <Tag style={{ margin: 0 }}>{aiCreditsWallet.currency}</Tag>
              )}
            </Space>
          }
          size="small"
          style={{ marginTop: 16 }}
          extra={
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => setTopUpModalVisible(true)}
            >
              {t("operator.governance.usage.top_up")}
            </Button>
          }
        >
          <Row gutter={24} align="middle">
            <Col span={5}>
              <Statistic
                title={t("operator.governance.usage.balance")}
                value={aiCreditsWallet.balance}
                prefix={aiCreditsWallet.currency === "TWD" ? "NT$" : "$"}
                valueStyle={{
                  color: aiCreditsWallet.balance > 0 ? "#3f8600" : "#cf1322",
                }}
              />
            </Col>
            <Col span={5}>
              <Statistic
                title={
                  <Space>
                    {t("operator.governance.usage.daily_budget")}
                    <EditOutlined
                      style={{
                        fontSize: 12,
                        color: "#1890ff",
                        cursor: "pointer",
                      }}
                      onClick={handleOpenBudgetModal}
                    />
                  </Space>
                }
                value={
                  aiCreditsWallet.daily_budget_unlimited
                    ? t("operator.governance.usage.unlimited")
                    : (aiCreditsWallet.daily_budget_limit ?? "-")
                }
                prefix={
                  aiCreditsWallet.daily_budget_unlimited
                    ? undefined
                    : aiCreditsWallet.currency === "TWD"
                      ? "NT$"
                      : "$"
                }
              />
            </Col>
            <Col span={5}>
              <Statistic
                title={t("operator.governance.usage.lifetime_purchased")}
                value={aiCreditsWallet.lifetime_purchased}
                prefix={aiCreditsWallet.currency === "TWD" ? "NT$" : "$"}
              />
            </Col>
            <Col span={9}>
              <Text
                type="secondary"
                style={{ fontSize: 12, display: "block", marginBottom: 4 }}
              >
                {t("operator.governance.usage.percentage")}
              </Text>
              <Progress
                percent={Math.min(aiCreditsWallet.percentage_used, 100)}
                status={
                  aiCreditsWallet.percentage_used > 100
                    ? "exception"
                    : aiCreditsWallet.percentage_used > 80
                      ? "active"
                      : "normal"
                }
                format={(pct) => `${aiCreditsWallet.percentage_used}%`}
              />
            </Col>
          </Row>
        </Card>
      )}

      {/* Top-up Modal */}
      <TopUpModal
        visible={topUpModalVisible}
        onClose={() => setTopUpModalVisible(false)}
        onSuccess={fetchData}
        t={t}
      />

      {/* Cancel Modal */}
      <Modal
        title={t("operator.governance.subscription.cancel_confirm_title")}
        open={cancelModalVisible}
        onOk={handleCancelSubscription}
        onCancel={() => setCancelModalVisible(false)}
        confirmLoading={updating}
        okText={t("operator.governance.subscription.confirm_cancel")}
        okButtonProps={{ danger: true }}
        cancelText={t("common.button.cancel")}
      >
        <p>{t("operator.governance.subscription.cancel_confirm_desc")}</p>
        <Alert
          message={t("operator.governance.subscription.cancel_notice")}
          type="warning"
          showIcon
        />
      </Modal>

      {/* Bank Transfer Checkout Modal */}
      <Modal
        title={
          <Space>
            <BankOutlined />
            {t("operator.governance.subscription.bank_transfer_checkout_title")}
          </Space>
        }
        open={checkoutModalVisible}
        onOk={handleBankTransferCheckout}
        onCancel={() => setCheckoutModalVisible(false)}
        confirmLoading={checkoutLoading}
        okText={t("operator.governance.subscription.generate_invoice")}
        cancelText={t("common.button.cancel")}
      >
        <Alert
          message={t(
            "operator.governance.subscription.bank_transfer_checkout_info",
          )}
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
        />
        {subscription && (
          <Descriptions bordered column={1} size="small">
            <Descriptions.Item label={t("operator.governance.plan.name")}>
              {t(
                `operator.governance.plan.types.${subscription.pricing_plan.plan_type}`,
              )}
            </Descriptions.Item>
            <Descriptions.Item
              label={t("operator.governance.subscription.billing_cycle")}
            >
              {t(
                `operator.governance.subscription.cycles.${subscription.billing_cycle}`,
              )}
            </Descriptions.Item>
            <Descriptions.Item
              label={t("operator.governance.subscription.amount_to_pay")}
            >
              <Text strong style={{ color: "#1890ff", fontSize: "18px" }}>
                NT$ {subscription.current_price?.toLocaleString()}
              </Text>
            </Descriptions.Item>
          </Descriptions>
        )}
        <Divider />
        <Text type="secondary">
          {t("operator.governance.subscription.bank_transfer_checkout_desc")}
        </Text>
      </Modal>

      {/* Daily Budget Modal */}
      <Modal
        title={t("operator.governance.usage.edit_daily_budget")}
        open={budgetModalVisible}
        onCancel={() => setBudgetModalVisible(false)}
        onOk={handleSaveBudget}
        confirmLoading={updatingBudget}
        okText={t("common.button.save")}
        cancelText={t("common.button.cancel")}
      >
        <div style={{ marginBottom: 16 }}>
          <Text type="secondary">
            {t("operator.governance.usage.daily_budget_desc")}
          </Text>
        </div>
        <Space direction="vertical" size="middle" style={{ width: "100%" }}>
          <div>
            <Button
              type={budgetUnlimited ? "primary" : "default"}
              onClick={() => {
                setBudgetUnlimited(true);
                setBudgetValue(null);
              }}
              style={{ marginRight: 8 }}
            >
              {t("operator.governance.usage.unlimited")}
            </Button>
            <Button
              type={!budgetUnlimited ? "primary" : "default"}
              onClick={() => {
                setBudgetUnlimited(false);
                setBudgetValue(
                  aiCreditsWallet?.daily_budget_limit > 0
                    ? aiCreditsWallet.daily_budget_limit
                    : 1000,
                );
              }}
            >
              {t("operator.governance.usage.custom_limit")}
            </Button>
          </div>
          {!budgetUnlimited && (
            <InputNumber
              value={budgetValue}
              onChange={setBudgetValue}
              min={1}
              style={{ width: "100%" }}
              prefix={aiCreditsWallet?.currency === "TWD" ? "NT$" : "$"}
              placeholder={t("operator.governance.usage.enter_daily_limit")}
            />
          )}
        </Space>
      </Modal>
    </div>
  );
};

export default Subscription;
